import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitHandler, useForm } from 'react-hook-form';
import { convertInCurrencyValue, gtag } from 'utility/functions';

import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { ChipRadioButton } from 'components/inputs/chipRadioButton/ChipRadioButton';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RichText } from 'components/richText';
import { TextInput } from 'components/inputs/textInput';
import maximalStyles from './LimitMaximal.module.scss';
import { setMaximal } from 'features/signup/signup';
import styles from 'components/page-content/registrati/registrati.module.scss';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type LabelsMaximals = {
  titleMaximals: string;
  descriptionMaximals: string;
  chipsLabel: string;
  anotherAmount: string;
  amountLimiter: Array<string>;
  labelMinLimitValue: string;
  labelMaxLimitValue: string;
  limitMaxValue: number;
  labelNoDecimal: string;
};
export type MaximalsProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back?: Dispatch<SetStateAction<boolean>>;
  labels: LabelsMaximals;
};

const formatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const LimitMaximal: React.FC<MaximalsProps> = ({ next, back, labels }) => {
  const state = useSelector((store: RootState) => store.signup);
  const dispatch = useAppDispatch();
  const { asPath } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);
  const labelImportoMinimo = useMemo(
    () => labels.labelMinLimitValue.replace('{{minValue}}', formatter.format(10)),
    [labels.labelMinLimitValue]
  );
  const labelImportoMassimo = useMemo(
    () => labels.labelMaxLimitValue.replace('{{maxValue}}', formatter.format(labels.limitMaxValue)),
    [labels.labelMaxLimitValue, labels.limitMaxValue]
  );

  const schemaValidation = z
    .object({
      maxValue: z.string().optional(),
      otherImports: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/, `Sono accettati solo numeri`)
        .optional()
        .refine(
          (value) => {
            if (!!value) {
              return Number(value.replace(',', '.')) >= 10;
            }
            return true;
          },
          {
            message: labelImportoMinimo,
          }
        )
        .refine(
          (value) => {
            if (!!value) {
              return Number(value.replace(',', '.')) < labels.limitMaxValue;
            }
            return true;
          },
          {
            message: labelImportoMassimo,
          }
        )
        .refine(
          (value) => {
            if (!!value) {
              const numericValue = Number(value.replace(',', '.'));
              return Math.floor(numericValue) === numericValue;
            }
            return true;
          },
          {
            message: labels.labelNoDecimal,
          }
        ),
    })
    .refine((schema) => schema.maxValue || schema.otherImports, {
      path: ['otherImports'],
      message: 'È necessario selezionare almeno un importo',
    });
  type SchemaValidationType = z.infer<typeof schemaValidation>;

  const { handleSubmit, control, setValue, resetField, formState, trigger } = useForm<SchemaValidationType>({
    resolver: zodResolver(schemaValidation),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const goNext: SubmitHandler<SchemaValidationType> = (data) => {
    dispatch(
      setMaximal({
        weeklyMaximal: data.maxValue ?? data.otherImports!,
      })
    );
    next(true);
  };

  useEffect(() => {
    if (state.weeklyMaximal) {
      !!labels.amountLimiter.filter((amount) => amount === state.weeklyMaximal).length
        ? setValue('maxValue', state.weeklyMaximal)
        : setValue('otherImports', state.weeklyMaximal ?? undefined);
      trigger('maxValue');
      trigger('otherImports');
    }
  }, [labels.amountLimiter, setValue, state.weeklyMaximal, trigger]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'limits') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'limits');
        gtag(
          { section: 'RegistrationStepLimit', detail: 'RegistrationStepLimit', event: 'RegistrationStepLimit' },
          true
        );
      }
    }
  }, [registrationUrl, asPath]);

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit(goNext, (data) => console.error(data))} className={styles.form}>
        <div className={styles.title}>{labels.titleMaximals}</div>
        <RichText containerClassName={styles.subTitle}>{labels.descriptionMaximals}</RichText>
        <div className={maximalStyles.chipsLabel}>{labels.chipsLabel}</div>
        <div className={maximalStyles.wrapperChips}>
          <ChipRadioButton
            className={maximalStyles.chipRadioButton}
            control={control}
            name="maxValue"
            options={labels.amountLimiter.map((amount) => ({
              label: convertInCurrencyValue(Number(amount), 'EUR', 0),
              value: amount,
            }))}
            onChange={() => resetField('otherImports')}
          />
          <TextInput
            label=""
            placeholder={labels.anotherAmount}
            control={control}
            name="otherImports"
            className={maximalStyles.customInput}
            onChange={() => {
              resetField('maxValue');
            }}
          />
        </div>
        <ButtonRegistrationWrapper isNextDisabled={!formState.isValid} handleGoBack={back!}></ButtonRegistrationWrapper>
      </form>
    </div>
  );
};
